import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageTitle, Panel, Field, TextArea, TextInput, Btn, StatCard } from "../components/ui";
import { useI18n } from "../i18n";
import { CL } from "../api";

// IFRS S2 — Climate-related Disclosures (ISSB)
// Governance • Strategy (transition plan, scenario analysis, physical & transition risks) •
// Risk management • Cross-industry metrics (GHG Scope 1/2/3, internal carbon price).
export default function IfrsS2() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { CL.getIfrsS2().then(setD).catch(() => {}); }, []);
  if (!d) return null;
  const a = d.auto || {};
  const up = (k) => (e) => { const v = e.target.value; setD((p) => ({ ...p, [k]: v })); };
  const num = (v) => (v === "" || v == null ? 0 : Number(v));

  const save = async () => {
    setSaving(true);
    try {
      await CL.putIfrsS2({
        climate_governance: d.climate_governance || "",
        transition_plan: d.transition_plan || "",
        physical_risks: d.physical_risks || "",
        transition_risks: d.transition_risks || "",
        scenario_analysis: d.scenario_analysis || "",
        cross_industry_metrics_note: d.cross_industry_metrics_note || "",
        internal_carbon_price: num(d.internal_carbon_price),
      });
      // Refetch so `auto` stats (internal_carbon_cost_eur, intensity) reflect the new ICP.
      const fresh = await CL.getIfrsS2();
      setD(fresh);
      toast.success(t("saved"));
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="ifrs-s2-page">
      <PageTitle title={t("ifrsS2Title")} subtitle={t("ifrsS2Desc")} />

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard testId="s2-s1" label={t("scope1")} value={(a.scope1_t ?? 0).toLocaleString()} sub={t("tCO2e")} />
        <StatCard testId="s2-s2" label={t("scope2")} value={(a.scope2_t ?? 0).toLocaleString()} sub={t("tCO2e")} accent="#2f8f5b" />
        <StatCard testId="s2-s3" label={t("scope3")} value={(a.scope3_t ?? 0).toLocaleString()} sub={t("tCO2e")} accent="#e08a1e" />
        <StatCard testId="s2-icp" label={t("internalCarbonCost")} value={`€ ${(a.internal_carbon_cost_eur ?? 0).toLocaleString()}`} accent="#a24012" />
      </div>

      <Panel className="max-w-3xl">
        <div className="mb-4 font-display text-lg text-[#14512f]">{t("ifrsS2SectionsTitle")}</div>

        <Field label={t("climateGovernance")}>
          <TextArea data-testid="s2-gov" value={d.climate_governance || ""} onChange={up("climate_governance")} />
        </Field>
        <div className="mt-4">
          <Field label={t("transitionPlan")}>
            <TextArea data-testid="s2-plan" value={d.transition_plan || ""} onChange={up("transition_plan")} />
          </Field>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <Field label={t("physicalRisks")}>
            <TextArea data-testid="s2-physical" value={d.physical_risks || ""} onChange={up("physical_risks")} />
          </Field>
          <Field label={t("transitionRisks")}>
            <TextArea data-testid="s2-transitionrisk" value={d.transition_risks || ""} onChange={up("transition_risks")} />
          </Field>
        </div>
        <div className="mt-4">
          <Field label={t("scenarioAnalysis")}>
            <TextArea data-testid="s2-scenario" value={d.scenario_analysis || ""} onChange={up("scenario_analysis")} />
          </Field>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <Field label={t("internalCarbonPrice")}>
            <TextInput type="number" data-testid="s2-icp" value={d.internal_carbon_price ?? ""} onChange={up("internal_carbon_price")} placeholder="80" />
          </Field>
          <Field label={t("crossIndustryNote")}>
            <TextInput data-testid="s2-cross" value={d.cross_industry_metrics_note || ""} onChange={up("cross_industry_metrics_note")} />
          </Field>
        </div>

        <div className="mt-6">
          <Btn onClick={save} disabled={saving} data-testid="s2-save">
            {saving ? "..." : t("save")}
          </Btn>
        </div>
      </Panel>
    </div>
  );
}
